import {
  Building2,
  ChevronDown,
  ChevronRight,
  FolderTree,
  Layers,
  MapPin,
  Plus,
  Users,
} from "lucide-react";
import { useState } from "react";

import { Badge } from "@/common/ui/badge";
import { Button } from "@/common/ui/button";
import { cn } from "@/lib/utils";

import { orgUnitTypeLabel } from "../org-unit-types";
import type { OrgUnit } from "../types";

const typeIcons: Record<string, typeof Building2> = {
  enterprise: Building2,
  division: Layers,
  department: FolderTree,
  team: Users,
  location: MapPin,
};

const typeColors: Record<string, string> = {
  enterprise: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30 dark:text-emerald-400",
  division: "bg-blue-500/10 text-blue-600 border-blue-500/30 dark:text-blue-400",
  department: "bg-violet-500/10 text-violet-600 border-violet-500/30 dark:text-violet-400",
  team: "bg-amber-500/10 text-amber-600 border-amber-500/30 dark:text-amber-400",
  location: "bg-rose-500/10 text-rose-600 border-rose-500/30 dark:text-rose-400",
};

const statusColors: Record<string, string> = {
  active: "bg-emerald-500",
  inactive: "bg-zinc-400",
  pending: "bg-amber-500",
};

interface OrgTreeNodeProps {
  node: OrgUnit;
  selectedId: string | null;
  onSelect: (node: OrgUnit) => void;
  onAddChild: (parentId: string) => void;
  depth?: number;
}

function OrgTreeNode({
  node,
  selectedId,
  onSelect,
  onAddChild,
  depth = 0,
}: OrgTreeNodeProps): React.ReactElement {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children.length > 0;
  const Icon = typeIcons[node.org_unit_type] ?? Building2;
  const isSelected = selectedId === node.id;

  return (
    <div className="select-none">
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- the row wraps nested expand/add-child buttons, so it cannot itself be a button; it stays mouse-selectable and full keyboard tree navigation is a separate a11y task */}
      <div
        data-testid={`org-tree-node-${node.id}`}
        className={cn(
          "group flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 transition-colors",
          isSelected ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
        )}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
        onClick={() => onSelect(node)}
      >
        {hasChildren ? (
          <button
            aria-label={expanded ? `Collapse ${node.name}` : `Expand ${node.name}`}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="rounded p-0.5 hover:bg-accent"
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        ) : (
          <span className="w-5" />
        )}

        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="relative flex items-center justify-center">
            <Icon className="h-4 w-4" />
            <span
              className={cn(
                "absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full ring-2 ring-background",
                statusColors[node.status]
              )}
            />
          </div>

          <span className="truncate text-sm font-medium">{node.name}</span>

          <Badge
            variant="outline"
            className={cn("h-5 px-1.5 py-0 text-[10px]", typeColors[node.org_unit_type])}
          >
            {orgUnitTypeLabel(node.org_unit_type)}
          </Badge>

          <Button
            variant="ghost"
            size="icon"
            className="ml-auto h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              onAddChild(node.id);
            }}
          >
            <Plus className="h-3 w-3" />
            <span className="sr-only">Add child unit</span>
          </Button>
        </div>
      </div>

      {hasChildren && expanded && (
        <div className="relative">
          <div
            className="absolute bottom-0 left-0 top-0 border-l border-border"
            style={{ marginLeft: `${depth * 20 + 18}px` }}
          />
          {node.children.map((child) => (
            <OrgTreeNode
              key={child.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              onAddChild={onAddChild}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface OrgTreeProps {
  tree: OrgUnit;
  selectedId: string | null;
  onSelect: (node: OrgUnit) => void;
  onAddChild: (parentId: string) => void;
}

export function OrgTree({
  tree,
  selectedId,
  onSelect,
  onAddChild,
}: OrgTreeProps): React.ReactElement {
  return (
    <div className="py-2">
      <OrgTreeNode
        node={tree}
        selectedId={selectedId}
        onSelect={onSelect}
        onAddChild={onAddChild}
      />
    </div>
  );
}
